import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr, enGB, ar } from 'date-fns/locale';
import { InspectionChatWrapper } from './InspectionChatWrapper';
import { QuoteDetailsModal } from '../inspections/QuoteDetailsModel';
import { InvoiceModal } from '../invoices/InvoiceModal';
import {
    Car, Bike, Clock, MapPin, MessageCircle, AlertTriangle, Calendar, Phone,
    CheckCircle, XCircle, PlayCircle, Eye, FileText, DollarSign, ExternalLink,
    Check, Ban, Database, Zap, TrendingUp
} from 'lucide-react';
import { Inspection, VehicleType } from '../../types/inspection';
import { inspectionService } from '../../services/inspectionService';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../hooks/useLanguage';
import { InvoiceDetailsModal } from '../invoices/InvoiceDetailsModal';
import { AudioPlayer } from './AudioPlayer';

interface InspectionListProps {
    inspections: Inspection[];
    title: string;
    showTitle?: boolean;
    onStatusChange: (id: string, status: string) => void;
    onRefresh?: () => void;
    creditBalance?: { standard: number; ai: number } | null;
    onCreditUpdate?: () => Promise<void>;
}

export const InspectionList: React.FC<InspectionListProps> = ({
                                                                  inspections, title, showTitle = true, onStatusChange, onRefresh, creditBalance, onCreditUpdate
                                                              }) => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [credits, setCredits] = useState<{ standard: number, ai: number } | null>(null);
    const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
    const [currentInspections, setCurrentInspections] = useState<Inspection[]>(inspections);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedInspectionForInvoice, setSelectedInspectionForInvoice] = useState<Inspection | null>(null);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

    const getDateLocale = () => {
        switch (currentLanguage) {
            case 'ar': return ar;
            case 'en': return enGB;
            default: return fr;
        }
    };

    useEffect(() => { setCurrentInspections(inspections); }, [inspections]);

    const reloadInspection = async (inspectionId: string) => {
        try {
            const updatedInspection = await inspectionService.getInspectionById(inspectionId);
            setCurrentInspections(prev => prev.map(insp => insp._id === inspectionId ? updatedInspection : insp));
            if (selectedInspection && selectedInspection._id === inspectionId) setSelectedInspection(updatedInspection);
            return updatedInspection;
        } catch (error) {
            console.error('Erreur lors du rechargement de l\'inspection:', error);
            return null;
        }
    };

    const handleRefresh = async () => {
        if (onRefresh) onRefresh();
        if (onCreditUpdate) await onCreditUpdate();
    };

    const getVehicleIcon = (type: VehicleType) => {
        switch (type) {
            case 'HYBRIDE':
            case 'ELECTRIQUE':
                return <Car className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />;
            default:
                return <Car className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />;
        }
    };

    const hasObdData = (inspection: Inspection): boolean => {
        return !!(inspection.obdData &&
            (inspection.obdData.errorCodes?.length > 0 ||
                (inspection.obdData.liveData && Object.keys(inspection.obdData.liveData).length > 0)));
    };

    const getObdSeverity = (inspection: Inspection): 'critical' | 'warning' | 'info' => {
        if (!hasObdData(inspection)) return 'info';
        const errorCodes = inspection.obdData?.errorCodes || [];
        const liveData = inspection.obdData?.liveData || {};
        const criticalCodes = errorCodes.filter(code => code.startsWith('P0') || code.startsWith('P1') || code.startsWith('P2'));
        if (criticalCodes.length > 0) return 'critical';
        if (liveData.batteryVoltage && liveData.batteryVoltage < 11.5) return 'critical';
        if (liveData.coolantTemp && liveData.coolantTemp > 105) return 'critical';
        if (liveData.fuelLevel && liveData.fuelLevel < 10) return 'warning';
        return errorCodes.length > 0 ? 'warning' : 'info';
    };

    const renderObdIndicator = (inspection: Inspection) => {
        if (!hasObdData(inspection)) {
            return (
                <div className={`flex items-center text-gray-400 text-xs ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Database className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                    <span>{t('inspectionList.obd.noData')}</span>
                </div>
            );
        }
        const severity = getObdSeverity(inspection);
        const errorCodesCount = inspection.obdData?.errorCodes?.length || 0;
        let colorClass = '';
        let icon = <Database className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />;
        let text = '';
        switch (severity) {
            case 'critical':
                colorClass = 'text-red-600 bg-red-50 border border-red-200';
                icon = <AlertTriangle className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />;
                text = t('inspectionList.obd.critical');
                break;
            case 'warning':
                colorClass = 'text-orange-600 bg-orange-50 border border-orange-200';
                icon = <Zap className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />;
                text = t('inspectionList.obd.available');
                break;
            case 'info':
                colorClass = 'text-blue-600 bg-blue-50 border border-blue-200';
                icon = <TrendingUp className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />;
                text = t('inspectionList.obd.normal');
                break;
        }
        return (
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${colorClass}`}>
                {icon}
                <span className="font-medium">{text}</span>
                <span className={`${isRTL ? 'mr-1' : 'ml-1'} opacity-75`}>
                    ({errorCodesCount > 0 ? `${errorCodesCount} ${errorCodesCount > 1 ? t('inspectionList.obd.errors') : t('inspectionList.obd.error')}` : ''})
                </span>
            </div>
        );
    };

    const statusColors: { [key: string]: string } = {
        pending: 'bg-yellow-100 text-yellow-800',
        accepted: 'bg-blue-100 text-blue-800',
        rejected: 'bg-red-100 text-red-800',
        in_progress: 'bg-indigo-100 text-indigo-800',
        completed: 'bg-green-100 text-green-800',
        seen: 'bg-purple-100 text-purple-800'
    };

    const getStatusLabel = (status: string) => t(`inspectionList.status.${status}`);

    const handleViewDetails = (inspection: Inspection) => {
        if (inspection.status === 'rejected') {
            toast.error(t('inspectionList.messages.rejectedNotAvailable'));
            return;
        }
        setSelectedInspection(inspection);
        try {
            inspectionService.viewInspectionDetails(inspection._id)
                .then(() => reloadInspection(inspection._id))
                .catch(error => {
                    console.error("Erreur non bloquante lors du marquage comme vue:", error);
                    if (creditBalance) setCredits(creditBalance);
                });
        } catch (e) {
            console.error("Erreur lors du marquage comme vue:", e);
            if (creditBalance) setCredits(creditBalance);
        }
    };

    const handleStatusChange = async (id: string, status: string) => {
        try {
            setLoading(true);
            await onStatusChange(id, status);
            await reloadInspection(id);
            if (status === 'rejected' || status === 'completed') await handleRefresh();
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
            toast.error(t('inspectionList.messages.statusChangeError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCloseInspectionDetails = async () => {
        if (selectedInspection) await reloadInspection(selectedInspection._id);
        setSelectedInspection(null);
        await handleRefresh();
    };

    const getQuoteStatusLabel = (status: string) => t(`inspectionList.quotes.status.${status}`);

    const getQuoteStatusColor = (status: string) => {
        const quoteStatusColors: { [key: string]: string } = {
            draft: 'bg-gray-100 text-gray-800',
            cancelled: 'bg-gray-100 text-gray-800',
            sent: 'bg-blue-100 text-blue-800',
            accepted: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800'
        };
        return quoteStatusColors[status] || 'bg-gray-100 text-gray-800';
    };

    const hasAcceptedQuote = (inspection: Inspection) =>
        inspection.quotes?.some(quote => quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment') || false;

    const getAcceptedQuote = (inspection: Inspection) =>
        inspection.quotes?.find(quote => quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment');

    const handleViewQuoteDetails = (quoteId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedQuoteId(quoteId);
    };

    const handleInspectionUpdated = async (inspectionId: string) => {
        await reloadInspection(inspectionId);
    };

    const handleOpenInvoiceModal = (inspection: Inspection) => {
        const acceptedQuote = getAcceptedQuote(inspection);
        if (!acceptedQuote) {
            toast.error(t('inspectionList.messages.noAcceptedQuote'));
            return;
        }
        setSelectedInspectionForInvoice(inspection);
        setShowInvoiceModal(true);
    };

    return (
        <div className={`space-y-4 ${isRTL ? 'rtl' : ''}`}>
            {showTitle && <h2 className="text-xl sm:text-2xl font-bold mb-4">{title}</h2>}

            {currentInspections.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                    {t('inspectionList.noInspections')}
                </div>
            ) : (
                currentInspections.map((inspection) => {
                    const acceptedQuote = getAcceptedQuote(inspection);
                    const isRejected = inspection.status === 'rejected';

                    return (
                        <div key={inspection._id} className={`bg-white rounded-lg shadow-lg p-4 sm:p-6 ${isRejected ? 'opacity-60' : ''}`}>

                            {/* ── Card header ── */}
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div className="flex-shrink-0 mt-1">{getVehicleIcon(inspection.vehicleType)}</div>
                                    <div>
                                        <h3 className="text-base sm:text-lg font-semibold">
                                            {inspection.vehicleBrand} {inspection.vehicleModel} ({inspection.vehicleYear})
                                        </h3>
                                        <div dir={isRTL ? 'rtl' : 'ltr'}>
                                            <div className="text-sm font-medium">{inspection.clientName}</div>
                                            <div className="text-sm text-gray-600 flex items-center gap-2">
                                                <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                <span dir="ltr">{inspection.clientPhone}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 flex-wrap">
                                    <span className={`px-3 py-1 rounded-full text-xs sm:text-sm whitespace-nowrap ${statusColors[inspection.status]}`}>
                                        {getStatusLabel(inspection.status)}
                                    </span>
                                    {inspection.unreadMessages > 0 && (
                                        <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs whitespace-nowrap">
                                            {inspection.unreadMessages} {t('inspectionList.messages.newMessages')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* ── OBD / Urgency indicators ── */}
                            <div className={`mt-3 flex flex-wrap gap-2 items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                {inspection.urgency === 'high' && !isRejected && (
                                    <div className={`flex items-center text-red-600 bg-red-50 p-2 rounded border border-red-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <AlertTriangle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        <span className="text-sm font-medium">{t('inspectionList.urgency.high')}</span>
                                    </div>
                                )}
                                {!isRejected && renderObdIndicator(inspection)}
                                {!isRejected && inspection.audioExplanation?.url && (
                                    <AudioPlayer
                                        url={inspection.audioExplanation.url}
                                        duration={inspection.audioExplanation.duration}
                                        compact={true}
                                    />
                                )}
                                {isRejected && (
                                    <div className={`flex items-center text-red-600 bg-red-50 p-2 rounded border border-red-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <Ban className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        <span className="text-sm font-medium">{t('inspectionList.messages.inspectionRejected')}</span>
                                    </div>
                                )}
                            </div>

                            {/* ── Quotes ── */}
                            {inspection.quotes && inspection.quotes.length > 0 && !isRejected && (
                                <div className="mt-4 bg-indigo-50 p-3 rounded-md" dir={isRTL ? 'rtl' : 'ltr'}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <FileText className="w-5 h-5 text-indigo-600" />
                                        <h4 className="font-medium text-indigo-700">{t('inspectionList.quotes.title')}</h4>
                                    </div>
                                    <div className="space-y-2">
                                        {inspection.quotes.map((quote, index) => {
                                            const isAccepted = quote.status === 'accepted_with_payment' || quote.status === 'accepted_no_payment';
                                            const otherAccepted = hasAcceptedQuote(inspection) && !isAccepted;
                                            return (
                                                <div key={quote.quoteId} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-2 rounded-md gap-2 ${otherAccepted ? 'opacity-50' : ''}`}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <DollarSign className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                        <span className="text-sm">{t('inspectionList.quotes.quote')} #{index + 1}</span>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs ${getQuoteStatusColor(quote.status)}`}>
                                                            {getQuoteStatusLabel(quote.status)}
                                                        </span>
                                                        {isAccepted && (
                                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                                                <Check className="w-3 h-3" /> {t('inspectionList.quotes.accepted')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center justify-between sm:justify-end gap-3">
                                                        <div className="text-sm font-semibold" dir="ltr">
                                                            <span className={`font-medium flex ${isRTL ? 'flex-row-reverse' : ''} gap-1`}>
                                                                <span>{quote.totalTTC.toFixed(2)}</span>
                                                                <span dir="ltr"> {t('inspectionList.currency')} {t('inspectionList.quotes.includingVAT')}</span>
                                                            </span>
                                                        </div>
                                                        {!otherAccepted && (
                                                            <button onClick={(e) => handleViewQuoteDetails(quote.quoteId, e)} className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm whitespace-nowrap">
                                                                <ExternalLink className="w-4 h-4" />
                                                                {t('inspectionList.quotes.details')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Invoices ── */}
                            {inspection.invoices && inspection.invoices.length > 0 && !isRejected && (
                                <div className="mt-4 bg-green-50 p-3 rounded-md" dir={isRTL ? 'rtl' : 'ltr'}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <h4 className="font-medium text-green-700">{t('inspectionList.invoices.title')}</h4>
                                    </div>
                                    <div className="space-y-2">
                                        {inspection.invoices.map((invoice, index) => {
                                            const isPaid = invoice.status === 'paid';
                                            const isFinalized = invoice.status === 'finalized';
                                            return (
                                                <div key={invoice.invoiceId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white p-3 rounded-md border-l-4 border-green-500 gap-3">
                                                    <div className="flex items-start gap-3">
                                                        <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
                                                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-medium">{t('inspectionList.invoices.invoice')} #{index + 1}</span>
                                                                <span className={`px-2 py-0.5 rounded-full text-xs ${isPaid ? 'bg-blue-100 text-blue-800' : isFinalized ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                    {t(`inspectionList.invoices.status.${invoice.status}`)}
                                                                </span>
                                                            </div>
                                                            <div className="mt-1 text-xs text-gray-600 space-y-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span>{t('inspectionList.invoices.quoteAmount')}:</span>
                                                                    <span className={`font-medium flex ${!isRTL ? 'flex-row-reverse' : ''} gap-1`}>
                                                                        <span className="font-medium" dir="ltr">{invoice.quoteAmount.toFixed(2)}</span>
                                                                        <span className="text-gray-500">{t('inspectionList.currency')}</span>
                                                                    </span>
                                                                </div>
                                                                {invoice.additionalAmount > 0 && (
                                                                    <div className="flex items-center gap-2 text-orange-600">
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        <span>{t('inspectionList.invoices.additional')}:</span>
                                                                        <span className={`font-medium flex ${!isRTL ? 'flex-row-reverse' : ''} gap-1`}>
                                                                            <span className="font-medium" dir="ltr">+{invoice.additionalAmount.toFixed(2)}</span>
                                                                            <span>{t('inspectionList.currency')}</span>
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                                                        <div className="text-lg font-bold text-green-700" dir="ltr">
                                                            {invoice.totalTTC.toFixed(2)} <span>{t('inspectionList.currency')}</span>
                                                        </div>
                                                        {invoice.completedAt && (
                                                            <div className="text-xs text-gray-400">
                                                                {format(new Date(invoice.completedAt), 'PPp', { locale: getDateLocale() })}
                                                            </div>
                                                        )}
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setSelectedInvoiceId(invoice.invoiceId); }}
                                                            className="text-green-600 hover:text-green-800 flex items-center gap-1 text-sm whitespace-nowrap"
                                                        >
                                                            <ExternalLink className="w-4 h-4" />
                                                            {t('inspectionList.invoices.details')}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Description ── */}
                            <div className="mt-4">
                                <p className="text-gray-700 text-sm sm:text-base">{inspection.description}</p>
                            </div>

                            {/* ── Photos ── */}
                            {inspection.photos && inspection.photos.length > 0 && !isRejected && (
                                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {inspection.photos.slice(0, 4).map((photo, idx) => (
                                        <div key={photo.id || idx} className="relative">
                                            <img
                                                src={photo.url}
                                                alt={photo.description || t('inspectionList.vehicle.photo')}
                                                className="rounded-lg h-20 sm:h-24 w-full object-cover cursor-pointer"
                                                onClick={() => handleViewDetails(inspection)}
                                            />
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
                                                {format(new Date(photo.timestamp), 'HH:mm', { locale: getDateLocale() })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ── Meta info ── */}
                            <div className={`mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <Clock className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                    <span>{format(new Date(inspection.createdAt), 'PPp', { locale: getDateLocale() })}</span>
                                </div>
                                {inspection.distance && (
                                    <div className={`flex items-center text-indigo-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <span>{inspection.distance} {t('inspectionList.distance')}</span>
                                    </div>
                                )}
                            </div>

                            {inspection.notes && (
                                <div className="mt-4 bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
                                    {inspection.notes}
                                </div>
                            )}

                            {/* ── Actions ── */}
                            <div className="mt-4 pt-4 border-t" dir={isRTL ? 'rtl' : 'ltr'}>
                                <div className="flex flex-wrap gap-2">
                                    {inspection.status === 'pending' && (
                                        <button
                                            onClick={() => handleStatusChange(inspection._id, 'rejected')}
                                            className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                                            disabled={loading}
                                        >
                                            <XCircle className="w-4 h-4" />
                                            {t('inspectionList.actions.reject')}
                                        </button>
                                    )}
                                    {inspection.status === 'accepted' && (
                                        <button
                                            onClick={() => handleStatusChange(inspection._id, 'in_progress')}
                                            className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                                            disabled={loading}
                                        >
                                            <PlayCircle className="w-4 h-4" />
                                            {t('inspectionList.actions.start')}
                                        </button>
                                    )}
                                    {inspection.status === 'in_progress' && (
                                        <button
                                            onClick={() => handleOpenInvoiceModal(inspection)}
                                            className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                                            disabled={loading}
                                        >
                                            <CheckCircle className="w-4 h-4" />
                                            {t('inspectionList.actions.finalizeInvoice')}
                                        </button>
                                    )}
                                    {inspection.status !== 'rejected' && (
                                        <button
                                            onClick={() => handleViewDetails(inspection)}
                                            disabled={loading}
                                            className={`inline-flex items-center gap-2 px-3 py-2 sm:px-4 rounded hover:bg-gray-200 disabled:opacity-50 text-sm ${
                                                hasObdData(inspection) ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-700'
                                            }`}
                                        >
                                            <Eye className="w-4 h-4" />
                                            {loading ? t('inspectionList.loading') : hasObdData(inspection) ? t('inspectionList.actions.viewDetailsObd') : t('inspectionList.actions.viewDetails')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

            {/* ── Modals (unchanged) ── */}
            {selectedInspection && (
                <InspectionChatWrapper
                    inspection={selectedInspection}
                    garageId={JSON.parse(localStorage.getItem('user') || '{}')._id || ''}
                    garageName={JSON.parse(localStorage.getItem('user') || '{}').garageName || ''}
                    onClose={handleCloseInspectionDetails}
                    onCreateQuote={() => { console.log('Création du devis...'); }}
                    onStatusChange={handleStatusChange}
                    credits={credits}
                    onInspectionUpdated={handleInspectionUpdated}
                />
            )}
            {selectedQuoteId && (
                <QuoteDetailsModal quoteId={selectedQuoteId} onClose={() => setSelectedQuoteId(null)} />
            )}
            {showInvoiceModal && selectedInspectionForInvoice && getAcceptedQuote(selectedInspectionForInvoice) && (
                <InvoiceModal
                    inspection={selectedInspectionForInvoice}
                    acceptedQuote={getAcceptedQuote(selectedInspectionForInvoice)!}
                    onClose={() => { setShowInvoiceModal(false); setSelectedInspectionForInvoice(null); }}
                    onInvoiceCreated={async (invoiceId) => {
                        setShowInvoiceModal(false);
                        toast.success(t('inspectionList.messages.invoiceCreated'));
                        if (selectedInspectionForInvoice) await reloadInspection(selectedInspectionForInvoice._id);
                        setSelectedInspectionForInvoice(null);
                        await handleRefresh();
                    }}
                />
            )}
            {selectedInvoiceId && (
                <InvoiceDetailsModal invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />
            )}
        </div>
    );
};